import { useState } from "react";
import ICONS from "../../../assets/Icons";
import { IntervalConfiguration } from "../../../utils/configurationProvider";
import formatCurrency from "../../../utils/CurrencyFormater";
import IconRoundButton from "../CommonButtons/IconRoundButton";
import SVGComponent from "../SVGComponent";

/**
 * @typedef {Object} InfoBoxRow
 * @property {string} label - Row label
 * @property {number} value - Numeric value
 * @property {"add"|"subtract"|"multiply"|"divide"|null} operation - Operation for next row (optional)
 * @property {string} [icon] - Optional custom icon URL
 * @property {string} [imgColor] - Optional custom icon color
 * @param {boolean} props.closeiconisDisabled           -//Disable state for the close button.
 * @param {React.Ref} props.closeiconbuttonRef          //Ref forwarded to the close button.
 * @typedef {Object} CommonInfoBoxProps
 * @property {string} headerTitle - Popup header title
 * @property {number} transactionAmount - Top transaction amount
 * @property {string} [transactionAmountColor] - Color class for top transaction amount
 * @property {InfoBoxRow[]} rows - Array of rows (debit, tax, charge)
 * @property {string} [headerFontColor] - Header color class
 * @property {string} [headerFontWeight] - Header font weight
 * @property {string} [textSize] - Header font size
 * @property {() => void} onClosepop - Close popup callback
 * @property {string} [popupBgColor] - Popup background color class
 */

const CommonInfoBox = ({
  headerTitle,
  transactionAmount,
  transactionAmountColor,
  rows,
  headerFontColor,
  headerFontWeight,
  textSize,
  onClosepop,
  popupBgColor,
  transactionLabel,
  finalAmountLabel,
  closeiconisDisabled,
  closeiconbuttonRef,
}) => {
  const [animate, setAnimate] = useState(false);
  // Calculate final credited amount dynamically
  const finalAmount =
    Number(
      rows?.reduce((acc, row) => {
        const value = Number(row.value) || 0;
        switch (row.operation) {
          case "add":
            return acc + value;
          case "subtract":
            return acc - value;
          case "multiply":
            return acc * value;
          case "divide":
            return acc / value;
          default:
            return acc + value; // add by default if no operation
        }
      }, 0),
    ) || 0;

  return (
    <div
      className="flex w-width-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal px-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`h-fit w-full Qmd:w-small-popup-width flex flex-col rounded-border-radius-md ${animate ? "Qmd:animate-slideInFromLeftSmallPopUp" : "Qmd:animate-slideInFromRightSmallPopUp"}  bg-${popupBgColor || "primary-bg"}`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-padding-md">
          <p
            className={`text-${headerFontColor || "text-primary"} text-${textSize || "font-sm"} font-${headerFontWeight || "semibold"}`}
          >
            {headerTitle}
          </p>
          <IconRoundButton
            enabledBgColor="var(--primary-nav-act)"
            isLoading={false}
            handleFunction={() => {
              if (!closeiconisDisabled) {
                setAnimate(true);
                setTimeout(() => {
                  onClosepop();
                }, IntervalConfiguration.intervalExtraSmall);
              }
            }}
            title="Close"
            isDisabled={closeiconisDisabled}
            buttonRef={closeiconbuttonRef}
            icon={ICONS?.CLOSE}
            hoverOption="white"
          />
        </div>

        {/* Top Transaction Amount */}
        <div className="px-padding-md">
          <div className="flex justify-between items-start gap-gap-sm">
            <span className="text-font-md text-text-secondary font-normal flex-1 leading-tight">
              {transactionLabel}
            </span>
            <span
              className={`text-font-md font-semibold text-${transactionAmountColor || "settled"}`}
            >
              INR {formatCurrency(transactionAmount)}
            </span>
          </div>
        </div>
        <div className="my-margin-sm mx-margin-md border-b border-solid border-primary-nav"></div>
        {/* Body Section */}
        <div className="px-padding-md">
          {rows?.map((row, index) => (
            <div
              key={index}
              className="flex flex-col"
            >
              <div className="flex justify-between">
                <span className="text-font-md text-text-secondary font-normal flex-1 leading-tight">
                  {row.label}
                </span>
                <span className="text-font-md text-text-primary text-right font-semibold">
                  INR {formatCurrency(row.value)}
                </span>
              </div>

              {/* Operation icon */}
              {row.icon && index < rows.length - 1 && (
                <div className="flex justify-center my-margin-sm">
                  <SVGComponent
                    color={row.iconColor}
                    height="var(--icon-md)"
                    width="var(--icon-md)"
                    url={row.icon}
                    alt="icon"
                  />
                </div>
              )}
            </div>
          ))}
          <div className="mt-margin-sm border-t border-solid border-primary-nav"></div>
          {/* Equal icon */}
          <div className="flex justify-center my-margin-sm">
            <img
              src={ICONS?.EQUAL_WITH_ROUNDNESS}
              className="w-icon-md h-icon-md"
              alt=""
            />
          </div>

          {/* Final Amount */}

          <div className="flex justify-between mt-margin-xs mb-margin-md">
            <span className="text-font-md text-text-secondary font-normal flex-1 leading-tight">
              {finalAmountLabel}
            </span>
            <span className="text-font-md text-text-primary text-right font-semibold">
              INR {formatCurrency(finalAmount)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonInfoBox;
